import { TriangleAlert } from "lucide-react"
import * as React from "react"
import { useTranslation } from "react-i18next"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner"

export interface ConfirmOptions {
  title: React.ReactNode
  description?: React.ReactNode
  confirmLabel?: React.ReactNode
  cancelLabel?: React.ReactNode
  /** Red confirm button + warning icon. */
  destructive?: boolean
  /**
   * "Type the name to confirm": the confirm button stays disabled until the
   * user types exactly this text.
   */
  confirmText?: string
  /** Extra content between the description and the footer (e.g. a deletion preview). */
  children?: React.ReactNode
  /**
   * Runs when confirmed. While the promise is pending the dialog shows a
   * spinner; on rejection it stays open (the mutation already toasts).
   */
  onConfirm?: () => unknown | Promise<unknown>
}

export interface ConfirmDialogProps extends ConfirmOptions {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Controlled confirmation dialog for (destructive) actions. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  destructive = false,
  confirmText,
  children,
  onConfirm,
}: ConfirmDialogProps) {
  const { t } = useTranslation()
  const [typed, setTyped] = React.useState("")
  const [pending, setPending] = React.useState(false)
  const inputId = React.useId()

  React.useEffect(() => {
    if (open) setTyped("")
  }, [open])

  const blocked = confirmText !== undefined && typed !== confirmText

  const handleConfirm = async () => {
    if (blocked || pending) return
    try {
      setPending(true)
      await onConfirm?.()
      onOpenChange(false)
    } catch {
      /* keep the dialog open; the caller surfaces the error */
    } finally {
      setPending(false)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={(o) => !pending && onOpenChange(o)}>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          {destructive && (
            <AlertDialogMedia className="bg-destructive/10 text-destructive">
              <TriangleAlert />
            </AlertDialogMedia>
          )}
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        {children}
        {confirmText !== undefined && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              void handleConfirm()
            }}
          >
            <Field>
              <FieldLabel htmlFor={inputId} className="block font-normal">
                {t("confirm.typeToConfirm.before")}{" "}
                <span className="font-mono font-semibold">{confirmText}</span>{" "}
                {t("confirm.typeToConfirm.after")}
              </FieldLabel>
              <Input
                id={inputId}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                autoFocus
                aria-invalid={typed.length > 0 && blocked}
              />
              <FieldDescription className="text-xs">{t("confirm.irreversible")}</FieldDescription>
            </Field>
          </form>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>{cancelLabel ?? t("common.cancel")}</AlertDialogCancel>
          <Button
            variant={destructive ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={blocked || pending}
            data-testid="confirm-action"
            className={
              destructive
                ? "bg-destructive text-white hover:bg-destructive/90 dark:bg-destructive/80"
                : undefined
            }
          >
            {pending && <Spinner />}
            {confirmLabel ?? t("common.confirm")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = React.createContext<ConfirmFn | null>(null)

/**
 * Mount once near the root. Enables the imperative {@link useConfirm} API:
 * `if (await confirm({ title, destructive: true, onConfirm: () => m.mutateAsync(x) })) …`
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<(ConfirmOptions & { resolve: (v: boolean) => void }) | null>(null)
  const [open, setOpen] = React.useState(false)

  const confirm = React.useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        setState({ ...options, resolve })
        setOpen(true)
      }),
    [],
  )

  const resolvedRef = React.useRef(false)
  React.useEffect(() => {
    if (open) resolvedRef.current = false
  }, [open])

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <ConfirmDialog
          {...state}
          open={open}
          onConfirm={async () => {
            await state.onConfirm?.()
            resolvedRef.current = true
            state.resolve(true)
          }}
          onOpenChange={(o) => {
            setOpen(o)
            if (!o && !resolvedRef.current) state.resolve(false)
          }}
        />
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): ConfirmFn {
  const ctx = React.useContext(ConfirmContext)
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>")
  return ctx
}
